import { ChangeEvent } from 'react';
import { Text, Flex, Input, Icon, IconButton, InputGroup, InputRightElement } from '@chakra-ui/react';
import { MAX_SIZE_EAN } from 'shared/const';
import { IProductTitle, TProductSplit } from 'shared/types';
import AliasesTags from './AliasesTags';
import icons from 'shared/icons';
import { useTranslation } from 'helpers/languages/translations';

type TAttributeByTypeProps = {
    productToUpdate: IProductTitle;
    modalType: string;
    isUpc?: boolean;
    type: keyof IProductTitle;
    aliasesByFieldInput: any;
    setAliasesByFieldInput: any;
    setProductToUpdate: React.Dispatch<React.SetStateAction<IProductTitle>>;
    label?: string;
    aliases: {
        [key: string]: string[];
    };
    readonly: boolean;
};

const AttributeByType = ({
    productToUpdate,
    type,
    modalType,
    isUpc,
    aliasesByFieldInput,
    setAliasesByFieldInput,
    setProductToUpdate,
    label,
    aliases,
    readonly,
}: TAttributeByTypeProps) => {
    const translate = useTranslation();

    const PlusIcon = icons.plus;

    const handleChangePrimaryFields = (event: ChangeEvent<HTMLInputElement>, key: 'ean' | 'name') => {
        setProductToUpdate({ ...productToUpdate, [key]: event.target.value });
    };

    const handleChangeAliasInput = (event: ChangeEvent<HTMLInputElement>, key: keyof IProductTitle) => {
        setAliasesByFieldInput({ ...aliasesByFieldInput, [key]: event.target.value });
    };

    const handleAddAliasToSplit = (key: keyof IProductTitle) => {
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        if (aliasesByFieldInput[key]) {
            const productEntry = { ...(productToUpdate[key] as TProductSplit) };
            if (productEntry.val) {
                // if alias exists in list of aliases but not visible it means its inside the minus field
                if (productEntry.aliases?.includes(aliasesByFieldInput[key])) {
                    productEntry.minus = productEntry.minus?.filter((a) => a !== aliasesByFieldInput[key]);
                } else if (productEntry.plus) {
                    if (!productEntry.plus?.includes(aliasesByFieldInput[key])) {
                        productEntry.plus = [...productEntry.plus];
                        productEntry.plus.push(aliasesByFieldInput[key]);
                    }
                } else {
                    productEntry.plus = [aliasesByFieldInput[key]];
                }
            } else {
                productEntry.val = aliasesByFieldInput[key];
            }

            setProductToUpdate({ ...productToUpdate, [key]: productEntry } as IProductTitle);
            setAliasesByFieldInput({ ...aliasesByFieldInput, [key]: '' });
        }
    };

    switch (type) {
        case 'name':
        case 'ean': {
            const concreateType = type === 'ean' ? 'upc' : type;
            const title = isUpc ? concreateType : type;
            return (
                <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm">
                    <Text w="20%">{translate(`product.${title}`)}</Text>
                    <Flex flex="1">
                        <Input
                            disabled={modalType !== 'add'}
                            size="sm"
                            {...(type === 'ean' ? { maxLength: MAX_SIZE_EAN } : {})}
                            onChange={(e) => handleChangePrimaryFields(e, type)}
                            value={productToUpdate[isUpc ? concreateType : type]}
                        />
                    </Flex>
                </Flex>
            );
        }
        default: {
            const entry = productToUpdate[type] as TProductSplit;
            return (
                <Flex key={type} minH="40px" w="100%" alignItems="center" fontSize="sm" mb={1}>
                    <Text w="20%">
                        {label || translate('product.name_split_i', { idx: (type as string).split('_').pop() })}
                    </Text>
                    <Flex flex="1" justifyContent="space-between" alignItems="stretch">
                        <Flex
                            flexWrap="wrap"
                            w={readonly ? '100%' : '70%'}
                            border="1px solid #CAC5C4"
                            py={1}
                            px={2}
                            mr={readonly ? 0 : 2}
                        >
                            <AliasesTags
                                entry={entry}
                                type={type}
                                aliases={aliases}
                                productToUpdate={productToUpdate}
                                setProductToUpdate={setProductToUpdate}
                                readonly={readonly}
                            />
                        </Flex>
                        <Flex flex="1">
                            <InputGroup h="100%" minH="38px">
                                {!readonly && (
                                    <>
                                        <Input
                                            size="sm"
                                            h="100%"
                                            onChange={(event) => handleChangeAliasInput(event, type)}
                                            placeholder="Alias"
                                            value={aliasesByFieldInput[type]}
                                        />
                                        <InputRightElement h="100%" justifyContent="center" alignItems="center">
                                            <IconButton
                                                size="sm"
                                                aria-label="add-alias-split"
                                                icon={<Icon as={PlusIcon} />}
                                                onClick={() => handleAddAliasToSplit(type)}
                                            />
                                        </InputRightElement>
                                    </>
                                )}
                            </InputGroup>
                        </Flex>
                    </Flex>
                </Flex>
            );
        }
    }
};

export default AttributeByType;
